
import { get, isEmpty } from "lodash";
import { ADVISOR_EDUCATION_HUB_URL_FIELD_KEY, IAccount, IAccountOrgSiteIdProfile, INavigation, INavigationItem, IUser } from "@share/common-types";
import { Routes } from "@share/constants";
import { HasPermission, ViewManualBookingsPermission } from "@utils";
import { getPreferenceFromAccount } from "./accounts";

export const GetAccountProfile = (profiles: IAccountOrgSiteIdProfile[], organizationId: number, siteId: number) => {
  if (profiles?.length) {
    const profile = get(profiles.filter(p => p.organizationId === organizationId), '[0].profile') || get(profiles.filter(p => p.organizationId === siteId), '[0].profile');
    if (!isEmpty(profile)) {
      return profile;
    }
  }
  return null;
}

const GetMainMenuReplaceFlights = (items: INavigation) => {
  let main = items.main ? [...items.main] : items.main;
  return main.map((m: any) => {
    if (m.title === 'More Benefits' && m.childItems?.length) {
      const childItems = m.childItems.map((c: any) => {
        if (c.title === 'Flights') {
          return { ...c, url: 'https://www.flights.com/' };
        }
        return { ...c };
      });
      return { ...m, childItems };
    }
    return { ...m };
  });
}

const GetMainMenuLevartiAgent = (items: INavigation) => {
  let main = items.main ? [...items.main] : [];
  main = [
    {
      isPopup: false,
      title: 'Travel Agent',
      childItems: [
        { isPopup: false, title: 'Become Travel Agent', url: Routes.MLMBecomeAgent },
        {
          isPopup: false,
          excludeCheck: true,
          title: 'New Agents',
          url: Routes.MLMNewAgents,
        },
        {
          isPopup: false,
          excludeCheck: true,
          title: 'Experienced Agents',
          url: Routes.MLMExperiencedAgents,
        },
        {
          isPopup: false,
          excludeCheck: true,
          title: 'Exclusive access',
          url: Routes.MLMExclusiveAccess,
        },
        {
          isPopup: false,
          excludeCheck: true,
          title: 'Training',
          url: Routes.MLMTraining,
        },
        {
          isPopup: false,
          excludeCheck: true,
          title: 'Tools',
          url: Routes.MLMTechnology,
        },
      ],
    },
    ...items.main,
  ];

  return main;
}

const ADVISOR_EDUCATION_HUB_EXCLUDED_RSI_IDS = [1738538];

const GetMainMenuLevartiBasic = (account: IAccount, items: INavigation, user: IUser | null) => {
  // The hub target is an account-level preference (a Google Drive folder); with no
  // URL configured there is nothing to link to, so the entry is not rendered.
  const advisorEducationHubUrl = getPreferenceFromAccount(account, ADVISOR_EDUCATION_HUB_URL_FIELD_KEY);
  const showAdvisorEducationHub =
    !isEmpty(advisorEducationHubUrl) && !ADVISOR_EDUCATION_HUB_EXCLUDED_RSI_IDS.includes(items.rsiId);

  return [
    { isPopup: false, title: 'my.dashboard', url: `/dashboard` },
    ...(showAdvisorEducationHub
      ? [
          {
            isPopup: false,
            excludeCheck: true,
            title: 'advisor.education.hub',
            url: advisorEducationHubUrl as string,
          },
        ]
      : []),
    ...items.main,
    { isPopup: false, title: 'support.page', url: `/contact-us` },
    // The backoffice is embedded in an iframe, so the entry only makes sense for
    // users that are allowed to see manual bookings there.
    ...(HasPermission(user, ViewManualBookingsPermission)
      ? [{ isPopup: false, title: 'admin.page', url: `${Routes.Backoffice}` }]
      : []),
  ];
}

const FORCE_HTTPS_MENU_TITLES = ['ARN Hotels'];
const FORCE_EXTERNAL_MENU_TITLES = ['ARN Hotels'];

export const isForcedExternalMenuTitle = (title?: string): boolean =>
  !!title && FORCE_EXTERNAL_MENU_TITLES.includes(title);

const EnsureHttpsForKnownTitles = (items: INavigationItem[]): INavigationItem[] => {
  const fix = (item: INavigationItem): INavigationItem => {
    if (
      FORCE_HTTPS_MENU_TITLES.includes(item.title) &&
      item.url &&
      !/^https?:\/\//i.test(item.url)
    ) {
      return { ...item, url: `https://${item.url}` };
    }
    return item;
  };

  return items.map(m => {
    const fixed = fix(m);
    if (fixed.childItems?.length) {
      return { ...fixed, childItems: fixed.childItems.map(fix) };
    }
    return fixed;
  });
}

export const GetMainMenu = (account: IAccount, items: INavigation, user: IUser | null = null) => {
  let newMain = items.main;

  if (items.main?.length && account?.levartiOrgIds?.includes(items.organizationId)) {
    if (!items.isTravelAgent) {
      newMain = GetMainMenuLevartiAgent(items);
    } else {
      newMain = GetMainMenuLevartiBasic(account, items, user);
    }
  } else if (account?.isRSITemplate && items.main?.length) {
    newMain = GetMainMenuReplaceFlights(items);
  }
  
  return newMain?.length ? EnsureHttpsForKnownTitles(newMain) : newMain;
}

const HOW_TO_MENU = 'How To';
const MENU_TO_INCLUDE = ['Condos', 'Cruises', 'Getaways', 'Hotels', 'Homes', 'All Inclusives', 'Car Rentals', 'Flights', 'Daily Benefits', 'Elite Benefits', 'Certificates', HOW_TO_MENU];

export const GetMenuListToDisplay = (main: INavigationItem[], showMyAccount: boolean) => {
  const list: INavigationItem[] = [];
  
  if (main) {
    main.forEach(m => {
      if (!m.childItems?.length) {
        if (MENU_TO_INCLUDE.includes(m.title)) {
          list.push(m);
        }
      } else {
        m.childItems.forEach(c => {
          if (MENU_TO_INCLUDE.includes(c.title)) {
            const child = { ...c };
            if (c.title === HOW_TO_MENU) {
              child.title = 'Brio & Rci';
            }
            list.push(child);
          }
        });
      }
    });
  }

  if (showMyAccount) {
    list.push({
      title: 'My Account',
      url: `${Routes.RsiAccount}`,
    });
  } else {
    list.push({
      title: 'saved.properties',
      url: `${Routes.SavedPropertiesAccount}`,
    });
  }

  list.push({
    title: 'Contact Us',
    url: `${Routes.ContactUs}`,
  });

  return list;
}
